import {Injectable} from '@angular/core';
import {SingleResult} from '@app/interfaces';
import {environment} from '@src/environments/environment';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {AccountModulePackage} from '@app/interfaces/packages/account-module-package';
import {ModulePackage, ModulePackageBillingPeriod, ModulePackagePricing, ModulePackageTier} from '@app/interfaces/packages/module-package';
import {ModulePackageUsage} from '@app/interfaces/packages/module-package-usage';
import {FeatureFlagsSummary} from '@app/interfaces/packages/feature-flags-summary';
import {ApiCachingService} from '@app/services/api/api-caching.service';
import {
  PaymentInitializationResponse,
  PaymentStatusResponse
} from '@app/services/api/payment.service';

// Re-export unified types for backward compatibility
export { PaymentInitializationResponse, PaymentStatusResponse };

/**
 * Response from module activation endpoint
 */
export interface ModuleActivationResponse {
  message: string;
  accountModulePackageUuid: string;
}

/**
 * Free-unit quota for a module package (lifetime free units before activation).
 * Backend returns null data for module types without a free-unit counter.
 */
export interface ModuleQuotaStatus {
  used: number;
  limit: number;
  remaining: number | null; // null once unlocked (module active)
  unlocked: boolean;
  canCreate: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModulePackagesService {

  constructor(
    private http: HttpClient,
    private apiCaching: ApiCachingService
  ) {
  }

  /**
   * Get all available module packages for the account's country
   * @param moduleType Optional filter by module type (e.g., 'MOBILE_APP')
   */
  getAllModulePackages(moduleType?: string): Observable<SingleResult<ModulePackage[]>> {
    const params = moduleType ? { module_type: moduleType } : {};
    return this.http.get<SingleResult<ModulePackage[]>>(`${environment.apiUrl}/api/module-packages/all`, { params });
  }

  /**
   * Get active module packages for the authenticated account
   */
  getActiveModulePackages(): Observable<SingleResult<AccountModulePackage[]>> {
    return this.apiCaching.getCachedWithTTL(
      'activeModulePackages',
      60,
      this.http.get<SingleResult<AccountModulePackage[]>>(`${environment.apiUrl}/api/module-packages/active`)
    );
  }

  /**
   * Check if user has a mobile app plan selected
   * Returns plan details with permissions if plan exists, or indicates no plan selected
   */
  getMobileAppPlanStatus(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/module-packages/mobile-app-status`);
  }

  /**
   * CarPeak's per-vehicle price for this account, plus any negotiated volume deal.
   * `data` is null for modules that are not priced per vehicle.
   */
  getModulePricing(modulePackageUuid: string): Observable<SingleResult<ModulePackagePricing | null>> {
    return this.http.get<SingleResult<ModulePackagePricing | null>>(
      `${environment.apiUrl}/api/module-packages/${modulePackageUuid}/pricing`
    );
  }

  /**
   * Free-unit quota for a module: how many lifetime free units the account has
   * left before the module must be activated. `data` is null for module types
   * that don't define a free-unit counter.
   */
  getModuleQuota(modulePackageUuid: string): Observable<{ data: ModuleQuotaStatus | null }> {
    return this.http.get<{ data: ModuleQuotaStatus | null }>(
      `${environment.apiUrl}/api/module-packages/${modulePackageUuid}/quota`
    );
  }

  /**
   * Initialize payment authorization for module package
   * Creates Stripe SetupIntent and returns client secret for card authorization
   * Uses unified /payments/initialize endpoint
   *
   * Note: Returns PaymentInitializationResponse directly (not wrapped in SingleResult)
   * as the backend returns a flat JSON response
   */
  initializePayment(
    modulePackageUuid: string,
    hasSubscription: boolean = false,
    ref?: string,
    modulePackagePeriod: ModulePackageBillingPeriod | null = null
  ): Observable<PaymentInitializationResponse> {
    const payload: {
      type: string;
      modulePackageUuid: string;
      hasSubscription: boolean;
      modulePackagePeriod: ModulePackageBillingPeriod | null;
      ref?: string
    } = {
      type: 'module_package',
      modulePackageUuid,
      hasSubscription,
      modulePackagePeriod
    };
    if (ref) {
      payload.ref = ref;
    }
    return this.http.post<PaymentInitializationResponse>(
      `${environment.apiUrl}/api/payments/initialize`,
      payload
    );
  }

  /**
   * Activate a module package
   */
  activateModulePackage(
    modulePackageUuid: string,
    hasSubscription: boolean = false,
    ref?: string,
    modulePackagePeriod: ModulePackageBillingPeriod | null = null
  ): Observable<SingleResult<ModuleActivationResponse>> {
    const payload: {
      modulePackageUuid: string;
      hasSubscription: boolean;
      modulePackagePeriod: ModulePackageBillingPeriod | null;
      ref?: string;
    } = {
      modulePackageUuid,
      hasSubscription,
      modulePackagePeriod
    };
    if (ref) {
      payload.ref = ref;
    }
    return this.http.post<SingleResult<ModuleActivationResponse>>(
      `${environment.apiUrl}/api/module-packages/activate`,
      payload
    );
  }

  /**
   * Deactivate a module package
   */
  deactivateModulePackage(accountModulePackageUuid: string, feedback?: string): Observable<{ message: string }> {
    // The body is FLAT, not SingleResult-wrapped — this endpoint returns a plain
    // response()->json([...]) like /mobile-app-status does. It was previously
    // typed as SingleResult<{message}>, which no caller ever unwrapped.
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}/api/module-packages/${accountModulePackageUuid}/deactivate`,
      {feedback}
    );
  }

  /**
   * Get feature flags summary (merged from product + module packages)
   */
  getFeatureFlagsSummary(): Observable<SingleResult<FeatureFlagsSummary>> {
    return this.apiCaching.getCachedWithTTL(
      'featureFlagsSummary',
      60,
      this.http.get<SingleResult<FeatureFlagsSummary>>(`${environment.apiUrl}/api/module-packages/feature-flags-summary`)
    );
  }

  /**
   * Get every active package granting featureFlag for the account's country,
   * each annotated with whether the account currently owns it — used to offer
   * a tier upgrade (e.g. Basic -> PRO) without a failed request to intercept.
   */
  getTiersForFeatureFlag(featureFlag: string): Observable<SingleResult<ModulePackageTier[]>> {
    return this.http.get<SingleResult<ModulePackageTier[]>>(
      `${environment.apiUrl}/api/module-packages/tiers/${featureFlag}`
    );
  }

  /**
   * Get usage history for a module package
   */
  getModuleUsage(accountModulePackageUuid: string): Observable<SingleResult<ModulePackageUsage[]>> {
    return this.http.get<SingleResult<ModulePackageUsage[]>>(
      `${environment.apiUrl}/api/module-packages/${accountModulePackageUuid}/usage`
    );
  }

  /**
   * Get current billing period usage (live meter)
   */
  getCurrentUsage(accountModulePackageUuid: string): Observable<SingleResult<any>> {
    return this.http.get<SingleResult<any>>(
      `${environment.apiUrl}/api/module-packages/${accountModulePackageUuid}/usage/current`
    );
  }

  /**
   * Get usage history for billing periods
   */
  getUsageHistory(accountModulePackageUuid: string): Observable<SingleResult<ModulePackageUsage[]>> {
    return this.http.get<SingleResult<ModulePackageUsage[]>>(
      `${environment.apiUrl}/api/module-packages/${accountModulePackageUuid}/usage/history`
    );
  }

  /**
   * Clear cached data (call after activation/deactivation)
   */
  clearCache(): void {
    this.apiCaching.clearCached('activeModulePackages');
    this.apiCaching.clearCached('featureFlagsSummary');
  }
}
